import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {RequestState, RoundStatus} from "data/enums";
import type {ITournament, ICrunch} from "data/providers/json/json.provider";
import type {ICrunchStore} from "data/stores/crunch/crunch.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IUserStore} from "data/stores/user/user.store";
import {first, get, last} from "lodash";
import {getVideoUrl} from "data/utils";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";

export interface ICrunchController extends ViewController {
	requestState: RequestState;
	onPick: (crunchId: number) => void;

	get crunches(): ICrunch[] | undefined;
	get selectedCrunch(): number | null;
	get homeCrunch(): ICrunch | undefined;
	get awayCrunch(): ICrunch | undefined;
	get homeVideoUrl(): string;
	get awayVideoUrl(): string;
	get isLockout(): boolean;
	get homePercent(): number;
	get awayPercent(): number;
	get percentForRoundProgress(): number;
	get tip(): {
		[key: string]: number;
	} | null;
	get isRoundComplete(): boolean;
	get selectedRound(): number;
}

@injectable()
export class CrunchController implements ICrunchController {
	@observable tournament: ITournament | undefined;
	@observable requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.CrunchStore) private _crunchStore: ICrunchStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore
	) {
		makeAutoObservable(this);
	}

	get tournamentId() {
		return this.tournament?.id || 0;
	}

	get crunches() {
		return this._roundsStore.currentSelectedRound?.crunches;
	}

	get selectedCrunch() {
		return this._crunchStore.selectedCrunch;
	}

	get homeCrunch() {
		return first(this.crunches);
	}

	get awayCrunch() {
		return last(this.crunches);
	}

	get homeVideoUrl() {
		return getVideoUrl(get(this.homeCrunch, `video`, ""));
	}

	get awayVideoUrl() {
		return getVideoUrl(get(this.awayCrunch, `video`, ""));
	}

	get percentForRoundProgress(): number {
		return this.homePercent === 0 && this.awayPercent === 0 ? 50 : this.homePercent;
	}

	get isLockout(): boolean {
		return this._roundsStore.currentSelectedRound?.status !== RoundStatus.Scheduled;
	}

	get tip() {
		return get(this._predictionsStore.tips, "crunches", null);
	}

	get homePercent(): number {
		const homeCrunchId = get(this.homeCrunch, "id", 0);
		const value = get(this.tip, homeCrunchId, 0).toFixed();
		return parseInt(value);
	}

	get awayPercent(): number {
		const awayCrunchId = get(this.awayCrunch, "id", 0);
		const value = get(this.tip, awayCrunchId, 0).toFixed();
		return parseInt(value);
	}

	get isRoundComplete(): boolean {
		return this._roundsStore.currentSelectedRound?.status === RoundStatus.Complete;
	}

	get selectedRound(): number {
		return this._roundsStore.currentSelectedRound?.roundNumber ?? 0;
	}

	dispose(): void {
		return;
	}

	@action
	public onPick = (crunchId: number): void => {
		this._crunchStore.updateCrunch(crunchId);
	};

	init(): void {
		if (this._userStore.isAuthorized) {
			void this._crunchStore.fetchCrunch(this._roundsStore.currentSelectedRound?.id || 0);
		}
	}
}
